import React from 'react';
import ReactModal from 'react-modal';
import { CustomCheckBox, RegularButton } from 'pg-ui-library';
import { CloseIcon } from 'src/modules/login/components/mfa-modal/styles';
import closeSvg from 'src/assets/close.svg';
import { useFormik } from 'formik';
import * as yup from 'yup';

import emailSvg from 'src/assets/email.svg';
import { t } from 'i18next';
import CompanyService from 'src/services/company.services';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ICompany } from 'src/interfaces';
import {
  Input,
  Container,
  EmailIcon,
  Form,
  FormRow,
  FormRowLast,
  Header,
  InputContainer,
  InputLabel,
  ModalInput,
  ResetPasswordWrapper,
  StyledErrorWrap,
  CompanyGrid,
  CheckBoxContainer,
} from './styles';
import { userCreateExternalUser } from '../../mutations/external-user.mutation';

interface AddUsersProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const AddExternalUserModal: React.FC<AddUsersProps> = ({ isOpen, onRequestClose }) => {
  const createExternalUser = userCreateExternalUser();
  const queryClient = useQueryClient();

  const validationSchema = yup.object().shape({
    email: yup.string().required(t('global.form.required')).email(t('global.form.email')),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      selectedCompanies: [] as string[],
    },
    validationSchema,
    onSubmit: async values => {
      const { email, selectedCompanies } = values;
      await createExternalUser.mutateAsync({ user: { email, companies: selectedCompanies } });
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      onRequestClose();
    },
  });

  const { data } = useQuery({
    queryKey: ['users/companies'],
    queryFn: () => CompanyService.getCompanies(),
    enabled: true,
  });

  const companies = data?.data.items as ICompany[];

  return (
    <ReactModal
      appElement={document.getElementById('root') || undefined}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '30px',
          margin: 'auto',
          width: 'fit-content',
          height: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          padding: '0 20px 0 20px',
          background: '#F3F4F8',
          overflow: 'hidden',
        },
      }}
    >
      <Header>
        {t('settings.externalUsers.text2')}
        <CloseIcon src={closeSvg} onClick={onRequestClose} />
      </Header>
      <Form onSubmit={formik.handleSubmit}>
        <Container>
          <InputContainer>
            <FormRow>
              <ResetPasswordWrapper>
                <InputLabel>{t('login.form.email')}</InputLabel>
                <ModalInput>
                  <EmailIcon src={emailSvg} />
                  <Input
                    data-cy="reset-password-email-input"
                    type="email"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    placeholder={t('login.resetPassword.email')}
                    onChange={formik.handleChange}
                  />
                </ModalInput>
                {formik.errors.email && <StyledErrorWrap>{formik.errors.email}</StyledErrorWrap>}
              </ResetPasswordWrapper>
            </FormRow>
            <FormRow>
              <CompanyGrid>
                {companies?.map(company => (
                  <CheckBoxContainer key={company.legalEntityId}>
                    <CustomCheckBox
                      label=""
                      id={company.id}
                      name={company.legalEntityId}
                      checked={formik.values.selectedCompanies.includes(company.id)}
                      onChange={() => {
                        const selected = formik.values.selectedCompanies;
                        if (selected.includes(company.id)) {
                          formik.setFieldValue(
                            'selectedCompanies',
                            selected.filter(id => id !== company.id)
                          );
                        } else {
                          formik.setFieldValue('selectedCompanies', [...selected, company.id]);
                        }
                      }}
                    />
                    <span>{company.legalEntityId}</span>
                  </CheckBoxContainer>
                ))}
              </CompanyGrid>
            </FormRow>

            <FormRowLast>
              <RegularButton
                type="submit"
                onClick={() => formik.handleSubmit()}
                style={{ marginTop: '35px', marginLeft: '87%' }}
                disabled={formik.isSubmitting || !!formik.errors.email}
              >
                {t('global.form.save')}
              </RegularButton>
            </FormRowLast>
          </InputContainer>
        </Container>
      </Form>
    </ReactModal>
  );
};

export default AddExternalUserModal;
